import { useEffect, useState } from 'react';

import { Link, useNavigate } from 'react-router-dom';

import classNames from 'classnames';
import { Button, FieldLabel, Password, showSnackbar, TextInput } from 'fluxo-ui';
import { BuildingIcon, UserIcon } from 'fluxo-ui/icons';

import { AuthShell } from '../components/common';
import { ROUTES } from '../common/constants';
import { useAuthStore } from '../store/authStore';

const SignupPage: React.FC = () => {
    const navigate = useNavigate();
    const { signup, isAuthenticated, error, clearError } = useAuthStore();
    const [accountType, setAccountType] = useState<'individual' | 'organization'>('individual');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [orgName, setOrgName] = useState('');
    const [orgDomain, setOrgDomain] = useState('');
    const [errors, setErrors] = useState<{
        name?: string;
        email?: string;
        password?: string;
        confirmPassword?: string;
        orgName?: string;
        orgDomain?: string;
    }>({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            navigate(ROUTES.DASHBOARD);
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (error) {
            showSnackbar(error, 'Signup Failed', { type: 'error', timeout: 5000 });
            clearError();
        }
    }, [error, clearError]);

    const validate = () => {
        const newErrors: {
            name?: string;
            email?: string;
            password?: string;
            confirmPassword?: string;
            orgName?: string;
            orgDomain?: string;
        } = {};

        if (!name.trim()) {
            newErrors.name = 'Name is required';
        } else if (name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters';
        }

        if (!email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'Invalid email format';
        }

        if (!password) {
            newErrors.password = 'Password is required';
        } else if (password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (accountType === 'organization') {
            if (!orgName.trim()) {
                newErrors.orgName = 'Organization name is required';
            }
            if (orgDomain && !/^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/.test(orgDomain)) {
                newErrors.orgDomain = 'Invalid domain format';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsLoading(true);
        try {
            const signupData: any = {
                name: name.trim(),
                email: email.trim(),
                password,
            };

            if (accountType === 'organization') {
                signupData.organizationName = orgName.trim();
                if (orgDomain) {
                    signupData.organizationDomain = orgDomain.trim();
                }
                signupData.role = 'org_admin'; // First user becomes org admin
            }

            await signup(signupData);
            showSnackbar('Account created! Please check your email to verify your account before logging in.', 'Welcome to Testam', {
                type: 'success',
                timeout: 8000,
            });
            navigate(ROUTES.LOGIN);
        } catch (err: any) {
            // Error is handled in useEffect
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthShell>
            <div className="text-center mb-8">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Create your account</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">Join Testam and start creating exams</p>
            </div>

            {/* Account Type Toggle (segmented control) */}
            <div className="mb-6">
                <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700">
                    <button
                        type="button"
                        onClick={() => setAccountType('individual')}
                        className={classNames('flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm transition-colors', {
                            'bg-white dark:bg-slate-700 text-teal-600 dark:text-teal-400 font-medium shadow-sm':
                                accountType === 'individual',
                            'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200':
                                accountType !== 'individual',
                        })}
                    >
                        <UserIcon className="w-4 h-4" />
                        Individual
                    </button>
                    <button
                        type="button"
                        onClick={() => setAccountType('organization')}
                        className={classNames('flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm transition-colors', {
                            'bg-white dark:bg-slate-700 text-teal-600 dark:text-teal-400 font-medium shadow-sm':
                                accountType === 'organization',
                            'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200':
                                accountType !== 'organization',
                        })}
                    >
                        <BuildingIcon className="w-4 h-4" />
                        Organization
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {accountType === 'organization' && (
                    <>
                        <FieldLabel label="Organization Name" required error={errors.orgName}>
                            <TextInput
                                value={orgName}
                                onChange={(e) => {
                                    setOrgName(e.value);
                                    if (errors.orgName) setErrors({ ...errors, orgName: undefined });
                                }}
                                placeholder="Enter organization name"
                                disabled={isLoading}
                            />
                        </FieldLabel>
                        <FieldLabel label="Organization Domain" hint="Optional (e.g., company.com)" error={errors.orgDomain}>
                            <TextInput
                                value={orgDomain}
                                onChange={(e) => {
                                    setOrgDomain(e.value);
                                    if (errors.orgDomain) setErrors({ ...errors, orgDomain: undefined });
                                }}
                                placeholder="company.com"
                                disabled={isLoading}
                            />
                        </FieldLabel>
                    </>
                )}

                <FieldLabel label="Full Name" required error={errors.name}>
                    <TextInput
                        value={name}
                        onChange={(e) => {
                            setName(e.value);
                            if (errors.name) setErrors({ ...errors, name: undefined });
                        }}
                        placeholder="Enter your full name"
                        autoComplete="name"
                        disabled={isLoading}
                    />
                </FieldLabel>

                <FieldLabel label="Email" required error={errors.email}>
                    <TextInput
                        value={email}
                        onChange={(e) => {
                            setEmail(e.value);
                            if (errors.email) setErrors({ ...errors, email: undefined });
                        }}
                        placeholder="Enter your email"
                        autoComplete="email"
                        disabled={isLoading}
                    />
                </FieldLabel>

                <FieldLabel label="Password" required error={errors.password} hint="Must be at least 6 characters">
                    <Password
                        value={password}
                        onChange={(e) => {
                            setPassword(e.value);
                            if (errors.password) setErrors({ ...errors, password: undefined });
                        }}
                        placeholder="Create a password"
                        autoComplete="new-password"
                        disabled={isLoading}
                    />
                </FieldLabel>

                <FieldLabel label="Confirm Password" required error={errors.confirmPassword}>
                    <Password
                        value={confirmPassword}
                        onChange={(e) => {
                            setConfirmPassword(e.value);
                            if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: undefined });
                        }}
                        placeholder="Confirm your password"
                        autoComplete="new-password"
                        disabled={isLoading}
                    />
                </FieldLabel>

                <Button
                    type="submit"
                    label="Create Account"
                    variant="primary"
                    className="w-full"
                    isLoading={isLoading}
                    disabled={isLoading}
                />
            </form>

            <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
                Already have an account?{' '}
                <Link
                    to={ROUTES.LOGIN}
                    className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium transition-colors"
                >
                    Log in
                </Link>
            </div>
        </AuthShell>
    );
};

export default SignupPage;
